import { store } from '../core/store.js';
import { refs } from '../core/refs.js';
import { Storage } from '../core/storage.js';
import { runCode, exportCode, RUN_TRIGGERS } from './runner.js';
import { undoEditor, redoEditor, prettifyEditorCode, getLangFor } from './context-menu/editor-actions.js';
import { openSearchBar, closeAllSearchBars } from './context-menu/search.js';
import { closeContextMenu } from './context-menu/menu-renderer.js';
import { closeMenu } from './menu.js';
import { setLive } from './live-reflection.js';
import { isMobile } from './responsive.js';
import { panelSystem } from '../ui/panels.js';
import { disableInspector } from './preview-inspector.js';

const INDENT = '  ';
const PAIRS = {
  '(': ')',
  '[': ']',
  '{': '}',
  '"': '"',
  "'": "'",
  '`': '`'
};
const CLOSERS = new Set(Object.values(PAIRS));
const lineGutters = new WeakMap();
const lineCounts = new WeakMap();
const lineNumberFrames = new WeakMap();
let editorToolServices = {
  console: null
};

function editorEntries() {
  return [
    { editor: refs.htmlEditor, wrapper: refs.htmlEditor?.closest('.editor-wrapper') },
    { editor: refs.cssEditor, wrapper: refs.cssEditor?.closest('.editor-wrapper') },
    { editor: refs.jsEditor, wrapper: refs.jsEditor?.closest('.editor-wrapper') }
  ].filter((entry) => entry.editor && entry.wrapper);
}

function emitInput(editor) {
  editor.dispatchEvent(new Event('input', { bubbles: true }));
}

function replaceSelection(editor, text, cursorOffset = text.length) {
  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  editor.setRangeText(text, start, end, 'end');
  const cursor = start + cursorOffset;
  editor.setSelectionRange(cursor, cursor);
  emitInput(editor);
}

function handlePairInsertion(event, editor) {
  const opener = event.key;
  const closer = PAIRS[opener];
  if (!closer || event.metaKey || event.ctrlKey || event.altKey) return false;

  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  const selected = editor.value.slice(start, end);
  event.preventDefault();
  replaceSelection(editor, opener + selected + closer, 1 + selected.length);
  return true;
}

function handleCloserSkip(event, editor) {
  if (!CLOSERS.has(event.key) || event.metaKey || event.ctrlKey || event.altKey) return false;
  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  if (start !== end || editor.value[start] !== event.key) return false;
  event.preventDefault();
  editor.setSelectionRange(start + 1, start + 1);
  return true;
}

function handlePairBackspace(event, editor) {
  if (event.key !== 'Backspace' || event.metaKey || event.ctrlKey || event.altKey) return false;
  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  if (start !== end || start === 0) return false;

  const before = editor.value[start - 1];
  const after = editor.value[start];
  if (PAIRS[before] !== after) return false;

  event.preventDefault();
  editor.setRangeText('', start - 1, start + 1, 'start');
  emitInput(editor);
  return true;
}

function handleTab(event, editor) {
  if (event.key !== 'Tab') return false;
  event.preventDefault();

  const start = editor.selectionStart;
  const end = editor.selectionEnd;
  const value = editor.value;

  if (start !== end && value.slice(start, end).includes('\n')) {
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const selected = value.slice(lineStart, end);
    const replacement = event.shiftKey
      ? selected.replace(new RegExp(`^${INDENT}`, 'gm'), '')
      : selected.replace(/^/gm, INDENT);
    editor.setRangeText(replacement, lineStart, end, 'select');
    emitInput(editor);
    return true;
  }

  if (event.shiftKey) return true;
  replaceSelection(editor, INDENT);
  return true;
}

function countLines(value) {
  let count = 1;
  for (let index = 0; index < value.length; index += 1) {
    if (value.charCodeAt(index) === 10) count += 1;
  }
  return count;
}

function buildLineNumberHtml(total) {
  let html = '';
  for (let line = 1; line <= total; line++) {
    html += `<span>${line}</span>`;
  }
  return html;
}

function syncLineNumberScroll(editor, gutter) {
  gutter.style.transform = `translateY(${-editor.scrollTop}px)`;
}

function updateLineNumbers(editor, gutter, options = {}) {
  const total = countLines(editor.value);
  const previous = lineCounts.get(editor);
  if (options.force || previous !== total) {
    gutter.innerHTML = buildLineNumberHtml(total);
    lineCounts.set(editor, total);
  }
  syncLineNumberScroll(editor, gutter);
}

function scheduleLineNumbers(editor, gutter, options = {}) {
  const pendingFrame = lineNumberFrames.get(editor);
  if (pendingFrame) window.cancelAnimationFrame(pendingFrame);

  const frame = window.requestAnimationFrame(() => {
    lineNumberFrames.delete(editor);
    updateLineNumbers(editor, gutter, options);
  });
  lineNumberFrames.set(editor, frame);
}

function attachLineNumbers(editor, wrapper) {
  wrapper.classList.add('with-line-numbers');
  const gutter = document.createElement('div');
  gutter.className = 'line-number-gutter';
  gutter.setAttribute('aria-hidden', 'true');
  wrapper.prepend(gutter);
  lineGutters.set(editor, gutter);

  updateLineNumbers(editor, gutter, { force: true });
  editor.addEventListener('input', () => scheduleLineNumbers(editor, gutter));
  editor.addEventListener('scroll', () => syncLineNumberScroll(editor, gutter));
}

export function refreshLineNumbers(editor = null, options = {}) {
  if (editor) {
    const gutter = lineGutters.get(editor);
    if (gutter) scheduleLineNumbers(editor, gutter, options);
    return;
  }

  editorEntries().forEach(({ editor: entryEditor }) => {
    const gutter = lineGutters.get(entryEditor);
    if (gutter) scheduleLineNumbers(entryEditor, gutter, options);
  });
}

function handleUndoRedo(event, editor) {
  const modified = event.metaKey || event.ctrlKey;
  if (!modified || event.altKey) return false;

  const key = event.key.toLowerCase();
  if (key === 'z') {
    event.preventDefault();
    if (event.shiftKey) {
      redoEditor(editor);
    } else {
      undoEditor(editor);
    }
    return true;
  } else if (key === 'y' && !event.shiftKey) {
    event.preventDefault();
    redoEditor(editor);
    return true;
  }
  return false;
}

let lastFocusedEditor = null;

export function setLastFocusedEditor(editor) {
  lastFocusedEditor = editor;
}

export function getActiveEditor() {
  if (typeof document !== 'undefined' && document.activeElement) {
    const activeEl = document.activeElement;
    if (activeEl === refs.htmlEditor || activeEl === refs.cssEditor || activeEl === refs.jsEditor) {
      return activeEl;
    }
  }
  if (isMobile()) {
    const activeTab = store.get('activeTab') || 'html';
    if (activeTab === 'css') return refs.cssEditor;
    if (activeTab === 'js') return refs.jsEditor;
    return refs.htmlEditor;
  }
  return lastFocusedEditor || refs.htmlEditor;
}

function registerEditorKeyHandlers(editor) {
  editor.addEventListener('focus', () => {
    lastFocusedEditor = editor;
  });
  editor.addEventListener('click', () => {
    lastFocusedEditor = editor;
  });
  editor.addEventListener('keydown', (event) => {
    lastFocusedEditor = editor;
    if (handleUndoRedo(event, editor)) return;
    if (handleTab(event, editor)) return;
    if (handlePairBackspace(event, editor)) return;
    if (handleCloserSkip(event, editor)) return;
    handlePairInsertion(event, editor);
  });
}

export function handleGlobalShortcut(event) {
  const modified = Boolean(event.metaKey || event.ctrlKey);
  const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';

  // 1. Escape: Dismiss search bars, context menus, inspector, focus, and quick menu
  if (event.key === 'Escape') {
    closeAllSearchBars();
    closeContextMenu();
    closeMenu();
    disableInspector();
    panelSystem.exitFocus();
    return;
  }

  // 2. Shift + Option/Alt + F: Format / Prettify active editor code
  if (event.shiftKey && event.altKey && !modified && key === 'f') {
    const editor = getActiveEditor();
    if (editor) {
      event.preventDefault();
      const lang = getLangFor(editor);
      if (lang) {
        const formatted = prettifyEditorCode(editor, lang);
        if (formatted) {
          editorToolServices.console?.system(`Formatted ${lang.toUpperCase()} code.`);
        } else {
          editorToolServices.console?.system(`${lang.toUpperCase()} code is too large to format.`);
        }
      }
    }
    return;
  }

  // 3. Alt + L or Cmd/Ctrl + Shift + R: Toggle Live preview
  if ((event.altKey && !event.shiftKey && !modified && key === 'l') ||
      (modified && event.shiftKey && !event.altKey && key === 'r')) {
    event.preventDefault();
    setLive(!store.get('liveEnabled'));
    return;
  }

  // 4. Cmd/Ctrl + 1-5: Switch active panel
  if (modified && !event.altKey && !event.shiftKey && ['1', '2', '3', '4', '5'].includes(event.key)) {
    event.preventDefault();
    const panelMap = {
      '1': { tab: 'html', editor: refs.htmlEditor, panel: refs.panelHtml },
      '2': { tab: 'css', editor: refs.cssEditor, panel: refs.panelCss },
      '3': { tab: 'js', editor: refs.jsEditor, panel: refs.panelJs },
      '4': { tab: 'preview', target: refs.previewIframe, panel: refs.panelPreview },
      '5': { tab: 'console', target: refs.consoleOutput, panel: refs.panelConsole }
    };
    const targetInfo = panelMap[event.key];
    if (targetInfo) {
      if (isMobile()) {
        store.set('activeTab', targetInfo.tab);
        if (targetInfo.editor) {
          setTimeout(() => targetInfo.editor.focus(), 50);
        }
      } else {
        if (targetInfo.panel && panelSystem.isMinimized(targetInfo.panel)) {
          panelSystem.toggleMinimize(targetInfo.panel);
        }
        if (targetInfo.editor) {
          targetInfo.editor.focus();
          lastFocusedEditor = targetInfo.editor;
        } else if (targetInfo.target) {
          targetInfo.target.focus();
        }
      }
    }
    return;
  }

  // 5. Cmd/Ctrl + F: Open Find / Search in active editor
  if (modified && !event.altKey && !event.shiftKey && key === 'f') {
    const editor = getActiveEditor();
    if (editor) {
      const wrapper = editor.closest ? editor.closest('.editor-wrapper') : editor.parentElement;
      if (wrapper) {
        event.preventDefault();
        openSearchBar(wrapper, editor);
      }
    }
    return;
  }

  // Below require modifier (Cmd or Ctrl) without Alt
  if (!modified || event.altKey) return;

  if (key === 'enter') {
    event.preventDefault();
    void runCode({ trigger: RUN_TRIGGERS.EXPLICIT });
  } else if (key === 's') {
    event.preventDefault();
    Storage.setCode(store.get('html'), store.get('css'), store.get('js'));
    // Saving refreshes the preview but never runs unrun JavaScript.
    void runCode({ trigger: RUN_TRIGGERS.IMPLICIT, silent: true, allowHotSwap: true });
    editorToolServices.console?.system('Workspace saved locally.');
  } else if (key === 'e') {
    event.preventDefault();
    exportCode();
  } else if (key === 'i') {
    event.preventDefault();
    refs.inspectBtn?.click();
  } else if (event.shiftKey && key === 'l') {
    event.preventDefault();
    editorToolServices.console?.clear();
  }
}

function registerGlobalShortcuts() {
  window.addEventListener('keydown', handleGlobalShortcut);
}

export function configureEditorTools(services = {}) {
  editorToolServices = {
    ...editorToolServices,
    ...services
  };
}

export function registerEditorTools(services = {}) {
  configureEditorTools(services);

  editorEntries().forEach(({ editor, wrapper }) => {
    attachLineNumbers(editor, wrapper);
    registerEditorKeyHandlers(editor);
  });

  const editorByKey = {
    html: refs.htmlEditor,
    css: refs.cssEditor,
    js: refs.jsEditor
  };
  Object.entries(editorByKey).forEach(([key, editor]) => {
    if (!editor) return;
    store.subscribe(key, () => refreshLineNumbers(editor));
  });
  registerGlobalShortcuts();
}
